import { Button, Col, Form, Input, Row, Space, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ContentWrapper } from "../../../Components/Layout/ContentWrapper";
import { useQuery } from "../../../Hooks/useQuery";

import { AdminEntry } from "../Constants";
import { subsidiaryApi } from "../../../Services";
import { useNotification } from "../../../Hooks/useNotification";
import { AdminRows, blankAdmin, findDuplicateAdmins } from "./AdminRows";
import { SuccessModal } from "../SuccessModal";
import workOrderStyles from "../../WorkOrder/WorkOrder.module.scss";

/**
 * Create / edit a Subsidiary/Division and its admins.
 *
 * Mounted at both the create and edit routes; `?id=` decides which, so the
 * edit screen is literally the add screen rather than a near-duplicate.
 */
export const CreateSubsidiary = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const id = query.get("id");

  const [subsidiary, setSubsidiary] = useState("");
  const [admins, setAdmins] = useState<AdminEntry[]>([blankAdmin(0)]);
  const [successOpen, setSuccessOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { openNotification } = useNotification();

  /* Edit mode: hydrate from the detail endpoint. */
  useEffect(() => {
    if (!id) return;
    subsidiaryApi
      .detail(id)
      .then((record: any) => {
        setSubsidiary(record?.subsidiaryName ?? "");
        const loaded = (record?.subsidiaryAdmins ?? []).map((admin: any, index: number) => ({
          key: `admin-${index}`,
          name: admin.userName ?? "",
          email: admin.email ?? "",
        }));
        setAdmins(loaded.length ? loaded : [blankAdmin(0)]);
      })
      .catch(() => undefined);
  }, [id]);

  /**
   * Friendlier copy for the clash the API reports as
   * "Subsidiary Already Exists! Please create new". The wording varies between
   * endpoints, so the test is on "exist" alone. Anything else is shown as the
   * API worded it.
   */
  const describe = (error: any) => {
    const message = String(error?.message ?? "").trim();
    if (/exist/i.test(message)) {
      return "Subsidiary/Division already exists! Please try with a new subsidiary/Division name.";
    }
    return message || "Please try again.";
  };

  const handleSubmit = () => {
    setIsSaving(true);
    const payload = {
      ...(id && { subsidiaryId: id }),
      subsidiaryName: subsidiary.trim(),
      subsidiaryAdmins: admins
        .filter((admin) => admin.name || admin.email)
        .map((admin) => ({ userName: admin.name, email: admin.email })),
    };
    const request = id
      ? subsidiaryApi.updateDefault(payload)
      : subsidiaryApi.createDefault(payload);

    request
      .then(() => setSuccessOpen(true))
      .catch((err: any) =>
        openNotification({
          title: id ? "Failed to update" : "Failed to create",
          message: describe(err),
          type: "error",
        })
      )
      .finally(() => setIsSaving(false));
  };

  const backTo = "/approval-management?activeTab=subsidiaryDivision";

  const isValid =
    subsidiary.trim().length > 0 &&
    admins.length > 0 &&
    admins.every((admin) => admin.name && admin.email) &&
    findDuplicateAdmins(admins).size === 0;

  return (
    <ContentWrapper
      customTitle={
        <Space align="start">
          <Icon
            onClick={() => navigate(backTo)}
            icon="akar-icons:arrow-left"
            className={workOrderStyles.backIcon}
          />
          <Typography.Title
            level={3}
            style={{ margin: 0 }}
          >
            {id ? "Edit Subsidiary/Division" : "Create Subsidiary/Division"}
          </Typography.Title>
        </Space>
      }
    >
      <Form layout="vertical">
        <Typography.Title level={4}>Subsidiary/Division Details</Typography.Title>
        <Row gutter={24}>
          <Col
            xs={24}
            sm={12}
            lg={8}
          >
            <Form.Item required label="Subsidiary/Division">
              <Input
                value={subsidiary}
                onChange={(e) => setSubsidiary(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Typography.Title
          level={4}
          style={{ marginTop: 16 }}
        >
          Subsidiary/Division Admins
        </Typography.Title>

        <AdminRows
          admins={admins}
          setAdmins={setAdmins}
        />

        <Row
          justify="end"
          gutter={16}
          style={{ marginTop: 32 }}
        >
          <Col>
            <Button onClick={() => navigate(backTo)}>Cancel</Button>
          </Col>
          <Col>
            <Button
              type="primary"
              disabled={!isValid}
              loading={isSaving}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Col>
        </Row>
      </Form>

      <SuccessModal
        open={successOpen}
        message="Approvers are added successfully"
        onClose={() => {
          setSuccessOpen(false);
          navigate(backTo);
        }}
      />
    </ContentWrapper>
  );
};
